/**
 * The form column of a math homework: whichever exercise the rail has selected. The prompt is the
 * shared content-block list addressed by `{ exerciseId }`; hint, explanation and the type are the
 * exercise's own; the type's sub-form is dispatched through `MATH_EXERCISE_EDITORS`.
 */
import { computed, ref } from 'vue'

import type { Component, ComputedRef } from 'vue'
import type { NativeSelectOption } from '@shared/ui/fields'
import type { ExerciseContentOwner, MathExercise, MathPromptTab } from './store/interfaces'

import { MathExerciseType } from './enums'
import { useAssignmentEditorStore } from './store'
import { getMathExerciseTypeLabel } from './assignment-template-options'
import { MATH_EXERCISE_EDITORS } from '../../ui/assignments/editor/math/exercises/registry'

/** What the player's KaTeX renders between `$…$` — linked from the prompt's info icon. */
export const KATEX_SYNTAX_URL = 'https://katex.org/docs/supported.html'

export const MATH_PROMPT_TABS: readonly { value: MathPromptTab, text: string }[] = [
  { value: 'editor', text: 'Editor' },
  { value: 'preview', text: 'Preview' },
]

const TYPE_CHANGE_CONFIRMATION
  = 'Changing the type clears this exercise\'s answers (the prompt, hint and explanation stay). Continue?'

/** What a type change throws away — only worth a question when something was written there. */
const TYPE_CONTENT_KEYS = ['options', 'answer', 'pairs', 'items', 'ticks', 'steps'] as const

const hasWrittenValue = (value: unknown): boolean => {
  if (typeof value === 'string') {
    return value.trim() !== ''
  }
  if (Array.isArray(value)) {
    return value.some(hasWrittenValue)
  }
  if (typeof value === 'object' && value !== null) {
    return Object.entries(value).some(([key, inner]) => key !== 'id' && hasWrittenValue(inner))
  }

  return false
}

const hasTypeContent = (exercise: MathExercise): boolean => {
  const record: Record<string, unknown> = exercise

  return TYPE_CONTENT_KEYS.some(key => hasWrittenValue(record[key]))
}

const isKnownType = (value: unknown): value is MathExerciseType =>
  Object.values(MathExerciseType).includes(value as MathExerciseType)

interface UseMathExerciseEditorReturn {
  refExercise: ComputedRef<MathExercise | null>
  /** `null` when nothing is selected. */
  refOwner: ComputedRef<ExerciseContentOwner | null>
  /** `Exercise 3`. */
  refLabel: ComputedRef<string>
  refHint: ComputedRef<string>
  refExplanation: ComputedRef<string>
  /** The select's bound value — `NativeSelect` shows its `value`, not the options' `selected`. */
  refType: ComputedRef<MathExerciseType | null>
  refTypeOptions: ComputedRef<NativeSelectOption[]>
  /**
   * `key` for the type select. `NativeSelect` only resyncs when its `value` prop CHANGES, and a
   * refused type change leaves the value as it was — so the select would keep showing the rejected
   * type. Bumped on a refusal to remount it; it also carries the exercise id.
   */
  refTypeSelectKey: ComputedRef<string>
  /** `null` for a stored type no editor knows — the form says so instead of rendering nothing. */
  refTypeEditor: ComputedRef<Component | null>
  refPromptTab: ComputedRef<MathPromptTab>
  setPromptTab: (value: MathPromptTab) => void
  setHint: (value: string) => void
  setExplanation: (value: string) => void
  setType: (value: string) => void
}

export const useMathExerciseEditor = (): UseMathExerciseEditorReturn => {
  const store = useAssignmentEditorStore()

  const refExercise = computed<MathExercise | null>(() => store.getSelectedExercise)

  const refOwner = computed<ExerciseContentOwner | null>(() => {
    const exercise = refExercise.value

    return exercise ? { exerciseId: exercise.id } : null
  })

  const refLabel = computed<string>(() => {
    const index = store.getSelectedExerciseIndex

    return index < 0 ? '' : `Exercise ${index + 1}`
  })

  const readString = (key: 'hint' | 'explanation'): string => {
    const value: unknown = refExercise.value?.[key]

    return typeof value === 'string' ? value : ''
  }

  const refType = computed<MathExerciseType | null>(() => {
    const type: unknown = refExercise.value?.type

    return isKnownType(type) ? type : null
  })

  const refTypeResets = ref<number>(0)

  const refTypeSelectKey = computed<string>(() => `${refExercise.value?.id ?? 'none'}-${refTypeResets.value}`)

  const refHint = computed<string>(() => readString('hint'))
  const refExplanation = computed<string>(() => readString('explanation'))

  const refTypeOptions = computed<NativeSelectOption[]>(() =>
    Object.values(MathExerciseType).map(value => ({
      id: value,
      value,
      text: getMathExerciseTypeLabel(value),
      selected: value === refExercise.value?.type,
    })))

  const refTypeEditor = computed<Component | null>(() => {
    const type: unknown = refExercise.value?.type

    return isKnownType(type) ? MATH_EXERCISE_EDITORS[type] : null
  })

  const withExercise = (write: (exerciseId: string) => void): void => {
    const exercise = refExercise.value

    if (exercise) {
      write(exercise.id)
    }
  }

  const setHint = (value: string): void => {
    withExercise(id => store.updateExercise(id, { hint: value }))
  }

  const setExplanation = (value: string): void => {
    withExercise(id => store.updateExercise(id, { explanation: value }))
  }

  const refPromptTab = computed<MathPromptTab>(() => store.getPromptTab)

  const setPromptTab = (value: MathPromptTab): void => {
    store.setPromptTab(value)
  }

  /** Asks first when the change would discard answers the author wrote. */
  const setType = (value: string): void => {
    const exercise = refExercise.value

    if (!exercise || !isKnownType(value) || value === exercise.type) {
      return
    }
    if (hasTypeContent(exercise) && typeof globalThis.confirm === 'function'
      && !globalThis.confirm(TYPE_CHANGE_CONFIRMATION)) {
      refTypeResets.value += 1
      return
    }
    store.changeExerciseType(exercise.id, value)
  }

  return {
    refExercise,
    refOwner,
    refLabel,
    refHint,
    refExplanation,
    refType,
    refTypeOptions,
    refTypeSelectKey,
    refTypeEditor,
    refPromptTab,
    setPromptTab,
    setHint,
    setExplanation,
    setType,
  }
}
